<template>
  <a-modal v-model="visible" title="Edit Tuition Enrollment" :footer="null" width="650px" @cancel="handleCancel" :afterClose="resetForm">
    <a-form :form="form" @submit="handleSubmit">
      <div class="mb-3 p-3 bg-light rounded" v-if="studentInfo">
        <strong>{{ studentInfo.name }}</strong>
        <span class="text-muted ml-2" v-if="studentInfo.code">{{ studentInfo.code }}</span>
        <span class="text-muted ml-2" v-if="studentInfo.classSection"> · {{ studentInfo.classSection }}</span>
      </div>

      <div class="row">
        <div class="col-md-6 col-12">
          <a-form-item label="Tuition Program">
            <a-select
              v-decorator="['tuition_program_id', { rules: [{ required: true, message: 'Please select program' }] }]"
              placeholder="Select Program"
              @change="handleProgramChange"
            >
              <a-select-option v-for="p in programs" :key="p.id" :value="p.id">
                {{ p.name }}
              </a-select-option>
            </a-select>
          </a-form-item>
        </div>
        <div class="col-md-6 col-12">
          <a-form-item label="Tuition Session (Batch)">
            <a-select
              v-decorator="['tuition_session_id']"
              placeholder="All Sessions / Select Session"
              allowClear
              :disabled="!tuitionSessions.length"
            >
              <a-select-option v-for="ts in tuitionSessions" :key="ts.id" :value="ts.id">
                {{ ts.name }} ({{ ts.default_start_time }} - {{ ts.default_end_time }})
              </a-select-option>
            </a-select>
          </a-form-item>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6 col-12">
          <a-form-item label="Start Date">
            <a-date-picker style="width: 100%" v-decorator="['start_date']" />
          </a-form-item>
        </div>
        <div class="col-md-6 col-12">
          <a-form-item label="End Date">
            <a-date-picker style="width: 100%" v-decorator="['end_date']" />
          </a-form-item>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6 col-12">
          <a-form-item label="Status">
            <a-select v-decorator="['status', { rules: [{ required: true, message: 'Please select status' }] }]">
              <a-select-option value="active">Active</a-select-option>
              <a-select-option value="inactive">Inactive</a-select-option>
            </a-select>
          </a-form-item>
        </div>
      </div>

      <div class="text-right mt-4">
        <a-button class="mr-2" @click="handleCancel">Cancel</a-button>
        <a-button type="primary" html-type="submit" :loading="loading">Update Enrollment</a-button>
      </div>
    </a-form>
  </a-modal>
</template>

<script>
import moment from 'moment'

export default {
  name: 'EditTuitionEnrollmentModal',
  data() {
    return {
      form: this.$form.createForm(this),
      loading: false,
      visible: false,
      recordId: null,
      studentInfo: null,
      programs: [],
      tuitionSessions: [],
    }
  },
  mounted() {
    this.$eventBus.$on('openEditTuitionEnrollment', record => {
      this.recordId = record.id
      this.visible = true
      this.fetchPrograms()

      // Build student info display
      this.studentInfo = {
        name: record.student ? `${record.student.first_name} ${record.student.last_name}`.trim() : 'Unknown',
        code: record.student ? record.student.student_code : '',
        classSection: this.buildClassSection(record),
      }

      // If record has a program, load its sessions first
      if (record.tuition_program_id) {
        this.fetchTuitionSessions(record.tuition_program_id, () => {
          this.setFormValues(record)
        })
      } else {
        this.$nextTick(() => {
          this.setFormValues(record)
        })
      }
    })
  },
  beforeDestroy() {
    this.$eventBus.$off('openEditTuitionEnrollment')
  },
  methods: {
    buildClassSection(record) {
      if (!record.school_enrollment) return ''
      const cls = record.school_enrollment.school_class ? record.school_enrollment.school_class.name : ''
      const sec = record.school_enrollment.section ? record.school_enrollment.section.name : ''
      return [cls, sec].filter(Boolean).join(' / ')
    },
    setFormValues(record) {
      this.$nextTick(() => {
        this.form.setFieldsValue({
          tuition_program_id: record.tuition_program_id,
          tuition_session_id: record.tuition_session_id || undefined,
          start_date: record.start_date ? moment(record.start_date, 'YYYY-MM-DD') : undefined,
          end_date: record.end_date ? moment(record.end_date, 'YYYY-MM-DD') : undefined,
          status: record.status || 'active',
        })
      })
    },
    fetchPrograms() {
      this.$http.get('/api/tuition/programs')
        .then(({ data }) => {
          this.programs = data.data || data
        })
    },
    fetchTuitionSessions(programId, callback) {
      this.$http.get(`/api/tuition/programs/${programId}/sessions`)
        .then(({ data }) => {
          this.tuitionSessions = data.data || data
          if (callback) callback()
        })
    },
    handleProgramChange(programId) {
      this.form.setFieldsValue({ tuition_session_id: undefined })
      this.tuitionSessions = []
      if (programId) {
        this.fetchTuitionSessions(programId)
      }
    },
    handleCancel() {
      this.visible = false
    },
    resetForm() {
      this.form.resetFields()
      this.studentInfo = null
      this.tuitionSessions = []
    },
    handleSubmit(e) {
      e.preventDefault()
      this.form.validateFields((err, values) => {
        if (!err) {
          this.loading = true
          const payload = {
            tuition_program_id: values.tuition_program_id,
            tuition_session_id: values.tuition_session_id || null,
            start_date: values.start_date ? values.start_date.format('YYYY-MM-DD') : null,
            end_date: values.end_date ? values.end_date.format('YYYY-MM-DD') : null,
            status: values.status,
          }

          this.$http.patch(`/api/tuition/enrollments/${this.recordId}`, payload)
            .then(() => {
              this.loading = false
              this.visible = false
              this.$emit('onEdited')
              this.$notification.success({
                message: 'Success',
                description: 'Enrollment updated successfully.',
              })
            })
            .catch(error => {
              this.loading = false
              this.$neoNotify.error(error)
            })
        }
      })
    },
  },
}
</script>
